import {Injectable} from '@angular/core';
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {from, Observable, switchMap, throwError} from 'rxjs';
import {Router} from '@angular/router';
import {NGXLogger} from 'ngx-logger';
import {catchError} from 'rxjs/operators';
import {SecurityConfigService} from '@twpub/shared/services/security-config.service';
import {SUPPRESS_ERROR_LOG_STATUSES} from './http-context-tokens';

const serverName = 'TermWebPublish Server';
@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  private redirectingToLogin = false;

  constructor(private router: Router, private logger: NGXLogger, private securityConfig: SecurityConfigService) {
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => this.handleError(error, req, next))
    );
  }

  private handleError(error: HttpErrorResponse, req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    let userMessage = '';
    const errorContext = `${req.method} ${req.urlWithParams}`;

    if (error.status === 0) {
      userMessage = `Unable to connect to ${serverName}.`;
    } else if (error.status === 401) {
      if (this.securityConfig.isExternalAuth() && this.securityConfig.isUserAuthenticatedExternally()) {
        this.logger.info('401 received, attempting silent token refresh');
        return from(this.securityConfig.silentRefresh()).pipe(
          switchMap(token => {
            if (token) {
              const retryReq = req.clone({
                setHeaders: {Authorization: `Bearer ${token}`}
              });
              return next.handle(retryReq);
            }
            this.logger.info('Silent refresh failed, redirecting to login');
            this.securityConfig.login();
            return throwError(() => error);
          })
        );
      }
      this.logger.info('User session has timed out, redirecting to login.');
      if (!this.redirectingToLogin) {
        this.redirectingToLogin = true;
        this.securityConfig.login();
      }
    } else if (error.status === 404) {
      userMessage = 'Resource not found.';
    } else if (error.status === 500) {
      userMessage = `Internal error in ${serverName}.`;
    } else {
      userMessage = `Unknown error when connecting to ${serverName}. ${JSON.stringify(error)}`;
    }
    if (userMessage && !this.isSuppressed(req, error.status)) {
      this.logger.error(`Error (${error.status}) when accessing ${errorContext}: ${userMessage}`);
    }
    return throwError(() => error);
  }

  private isSuppressed(req: HttpRequest<any>, status: number): boolean {
    return req.context.get(SUPPRESS_ERROR_LOG_STATUSES).includes(status);
  }
}
